import { Box, Circle, Stack, Text } from "@chakra-ui/react";
import {
  FileGalleryFragment,
  LinkFragment,
  MessageThreadFilesDrawerFragment,
} from "gql/graphql";
import { PaperclipIcon } from "modules/icons";
import LinkIcon from "modules/icons/link-icon";
import MessageThreadFilesDrawerAudioFile from "modules/messaging/drawers/files-drawer/message-thread-files-drawer-audio-file";
import MessageThreadFilesDrawerFile from "modules/messaging/drawers/files-drawer/message-thread-files-drawer-file";
import MessageThreadFilesDrawerLink from "modules/messaging/drawers/files-drawer/message-thread-files-drawer-link";
import React, { FC, memo, useCallback } from "react";
import { ItemProps, Virtuoso } from "react-virtuoso";
import colors from "app/colors";
import { MessageThreadFilesDrawerTabType } from "modules/utils/Constants";
import { isAudio } from "modules/utils/Functions";
import useMessageThreadFilesDrawer from "modules/hooks/use-message-thread-files-drawer";

interface IProps {
  messageThread?: MessageThreadFilesDrawerFragment | null;
  tabType: MessageThreadFilesDrawerTabType;
}

const FilesDrawerBody: React.FC<IProps> = ({ messageThread, tabType }) => {
  const { getFiles, links } = useMessageThreadFilesDrawer(messageThread);

  const renderFile = useCallback(
    (_: number, file: FileGalleryFragment) => {
      if (isAudio(file.contentType)) {
        return (
          <MessageThreadFilesDrawerAudioFile
            key={file.id}
            file={file}
            messageThread={messageThread}
          />
        );
      }
      return (
        <MessageThreadFilesDrawerFile
          key={file.id}
          messageThread={messageThread}
          file={file}
        />
      );
    },
    [messageThread],
  );

  const renderLink = useCallback(
    (_: number, link: LinkFragment) => (
      <MessageThreadFilesDrawerLink key={link.id} link={link} />
    ),
    [],
  );

  const files = getFiles(tabType);

  if (tabType === "LINKS" && links.length === 0) {
    return (
      <Stack spacing={4} alignItems="center" justifyContent="center" pt={4}>
        <Circle size="200px" bg={colors.whiteTertiary} mb={2}>
          <LinkIcon fill={colors.grayPrimary} boxSize={160} />
        </Circle>
        <Text fontFamily="Outfit" fontWeight={700} fontSize={26}>
          No Items to Display
        </Text>
        <Text fontWeight={600} fontSize={18}>
          Links in messages will appear here.
        </Text>
      </Stack>
    );
  }

  if (tabType === "LINKS") {
    return (
      <Virtuoso
        data={links}
        defaultItemHeight={118}
        itemContent={renderLink}
        components={{ Item: LinkItem }}
      />
    );
  }

  if (files.length === 0) {
    return (
      <Stack spacing={4} alignItems="center" justifyContent="center" pt={4}>
        <Circle size="200px" bg={colors.whiteTertiary} mb={2}>
          <PaperclipIcon
            fill={colors.grayPrimary}
            transform="rotate(45deg)"
            boxSize={160}
          />
        </Circle>
        <Text fontFamily="Outfit" fontWeight={700} fontSize={26}>
          No Items to Display
        </Text>
        <Text fontWeight={600} fontSize={18}>
          Attachments in this category will appear here.
        </Text>
      </Stack>
    );
  }

  return (
    <Virtuoso
      data={files}
      defaultItemHeight={118}
      itemContent={renderFile}
      components={{ Item: FileItem }}
    />
  );
};

export default memo(FilesDrawerBody);

const LinkItem: FC<ItemProps<LinkFragment>> = ({ children }) => (
  <Box px={4}>{children}</Box>
);

const FileItem: FC<ItemProps<FileGalleryFragment>> = ({ children }) => (
  <Box px={4}>{children}</Box>
);
